import {
  Typography,
  MenuItem,
  IconButton,
  Menu,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Tooltip,
} from "@mui/material";
import { useField } from "react-final-form";
import { FormatDateOptions, useIntl } from "react-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { EnhancedTable } from "@mb-pro-ui/components/table";

import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";

import { CellProps, Column, Row } from "react-table";

import { components } from "@helios/toolbox-types";
import { addDays, addHours, addWeeks, format, isAfter, isEqual, isValid } from "date-fns";
import { useEditedColor } from "../hooks/useEditedStyles";
import { DateTimeField } from "../components/form/DateTimeField";
import { UnitPermissionSelectWithFullSearchDialog } from "./UnitPermissionSelectWithFullSearchDialog";
type UnitPermission = components["schemas"]["UnitPermission"];

const deviceLimit = 1000;

export type UnitPermissionLike = Omit<UnitPermission, "expires"> & { expires?: Date | null; edited: boolean };
type PermissionMenuProps = {
  value: UnitPermissionLike | null;
  anchorEl: HTMLElement | null;
  onClose: (permission?: UnitPermissionLike) => void;
  initialValues: Map<number, Date | null>;
  onCustom: () => void;
};
enum PermissionExpireTypes {
  RESTORE = "restore",
  REVOKE = "revoke",
  UNTIL_REVOKATION = "until_revokation",
  HOUR = "hour",
  DAY = "day",
  WEEK = "week",
  CUSTOM = "custom",
}
const PermissionMenu = ({ value, anchorEl, onClose, initialValues, onCustom }: PermissionMenuProps) => {
  const open = Boolean(anchorEl);

  const { formatMessage } = useIntl();

  const handleClose = useCallback(
    (item?: PermissionExpireTypes, unitid?: number) => () => {
      if (!value || !item) {
        onClose();
        return;
      }
      if (item === PermissionExpireTypes.CUSTOM) {
        onCustom();
        onClose();
        return;
      }
      let expires: Date | null | undefined;
      switch (item) {
        case PermissionExpireTypes.REVOKE:
          expires = undefined;
          break;
        case PermissionExpireTypes.UNTIL_REVOKATION:
          expires = null;
          break;
        case PermissionExpireTypes.HOUR:
          expires = addHours(new Date(), 1);
          break;
        case PermissionExpireTypes.DAY:
          expires = addDays(new Date(), 1);
          break;
        case PermissionExpireTypes.WEEK:
          expires = addWeeks(new Date(), 1);
          break;
        case PermissionExpireTypes.RESTORE:
        default:
          expires = !unitid ? undefined : initialValues.get(unitid);
      }
      const { unitid: uid, company, hwinfo, edited } = value;
      onClose({ unitid: uid, expires, company, hwinfo, edited });
    },
    [initialValues, onClose, onCustom, value]
  );

  return (
    <Menu anchorEl={anchorEl} open={open} onClose={handleClose()}>
      {value?.expires !== undefined && (value?.expires === null || isAfter(value.expires, new Date())) && (
        <MenuItem onClick={handleClose(PermissionExpireTypes.REVOKE)}>{formatMessage({ defaultMessage: "Revoke permission" })}</MenuItem>
      )}
      {value?.edited && (
        <MenuItem onClick={handleClose(PermissionExpireTypes.RESTORE, value?.unitid)}>{formatMessage({ defaultMessage: "Restore to saved" })}</MenuItem>
      )}
      <Typography variant="subtitle2" sx={{ mx: 1 }}>
        {value?.expires !== undefined ? formatMessage({ defaultMessage: "Modify permission" }) : formatMessage({ defaultMessage: "Grant permission" })}
      </Typography>
      {value?.expires !== null && (
        <MenuItem onClick={handleClose(PermissionExpireTypes.UNTIL_REVOKATION)}>{formatMessage({ defaultMessage: "Until revokation" })}</MenuItem>
      )}
      <MenuItem onClick={handleClose(PermissionExpireTypes.HOUR)}>{formatMessage({ defaultMessage: "1 hour" })}</MenuItem>
      <MenuItem onClick={handleClose(PermissionExpireTypes.DAY)}>{formatMessage({ defaultMessage: "1 day" })}</MenuItem>
      <MenuItem onClick={handleClose(PermissionExpireTypes.WEEK)}>{formatMessage({ defaultMessage: "1 week" })}</MenuItem>
      <MenuItem onClick={handleClose(PermissionExpireTypes.CUSTOM)}>{formatMessage({ defaultMessage: "Custom" })}</MenuItem>
    </Menu>
  );
};

type CustomDateDialogProps = {
  open: boolean;
  onClose: () => void;
  onDateSelect: (value: Date) => void;
};
const CustomDateDialog = ({ open, onClose, onDateSelect }: CustomDateDialogProps) => {
  const [value, setValue] = useState("");
  const [valid, setValid] = useState(true);

  const { formatMessage } = useIntl();

  const onValueChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setValue(event.target.value), []);

  const onConfirm = useCallback(() => {
    if (!valid) {
      return;
    }
    const dateValue = new Date(value);
    onDateSelect(dateValue);
    onClose();
  }, [onClose, onDateSelect, valid, value]);

  useEffect(() => setValid(isValid(new Date(value))), [value]);

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{formatMessage({ defaultMessage: "Custom date" })}</DialogTitle>
      <DialogContent>
        <DialogContentText>{formatMessage({ defaultMessage: "Select the date and time." })}</DialogContentText>
        <DateTimeField
          label={formatMessage({ defaultMessage: "Valid until" })}
          placeholder={format(new Date(), "yyyy.MM.dd. HH:mm:ss")}
          shrink={true}
          onChange={onValueChange}
          valid={valid}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onConfirm} disabled={!valid}>
          {formatMessage({ defaultMessage: "Set" })}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const dateFormatOptions: FormatDateOptions = {
  dateStyle: "short",
  timeStyle: "medium",
};

type UserUnitPermissionsSubpageProps = {
  userDataQueryStatus: "error" | "loading" | "idle" | "success";
};
export const UserUnitPermissionsSubpage = ({ userDataQueryStatus }: UserUnitPermissionsSubpageProps) => {
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [unitSelectDialogOpen, setUnitSelectDialogOpen] = useState(false);
  const {
    input,
    meta: { initial = null },
  } = useField<UnitPermission[] | null>("unit_permissions");
  const initialValuesMap = useMemo(() => {
    const map = new Map<number, Date | null>();
    initial?.forEach(({ unitid, expires }) => map.set(unitid, expires ? new Date(expires) : null));

    return map;
  }, [initial]);

  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [permissionMenuValue, setPermissionMenuValue] = useState<null | UnitPermissionLike>(null);

  const { formatMessage, formatDate } = useIntl();
  const theme = useTheme();
  const { text: editedColor, background: editedBackground } = useEditedColor();

  const sortedPermissions = useMemo(() => {
    const permissions: Map<number, UnitPermissionLike> = new Map();

    if (!Array.isArray(input.value)) {
      return [];
    }

    input.value.forEach((item) => {
      let edited = !initialValuesMap.has(item.unitid);
      if (!edited && initialValuesMap.get(item.unitid) !== null && item.expires !== null) {
        edited = !isEqual(initialValuesMap.get(item.unitid) as Date, new Date(item.expires));
      } else if (!edited) {
        edited =
          (initialValuesMap.get(item.unitid) === null || item.expires === null) && !(initialValuesMap.get(item.unitid) === null && item.expires === null);
      }

      const { unitid, expires: expiresStr, company, hwinfo } = item;
      const expires = expiresStr !== null ? new Date(expiresStr) : expiresStr;
      permissions.set(unitid, { unitid, expires, company, hwinfo, edited });
    });

    if (initial) {
      initial.forEach((item) => {
        if (!permissions.has(item.unitid)) {
          const { unitid, company, hwinfo } = item;
          permissions.set(unitid, { unitid, expires: undefined, company, hwinfo, edited: true });
        }
      });
    }

    return Array.from(permissions.values()).sort(({ expires: a }, { expires: b }) => {
      if (a === b) {
        return 0;
      }
      if (a === null || b === undefined) {
        return -1;
      }
      if (b === null || a === undefined) {
        return 1;
      }
      return b.getTime() - a.getTime();
    });
  }, [initial, initialValuesMap, input.value]);

  const openCustomDialog = useCallback(() => setCustomDialogOpen(true), []);
  const closeCustomDialog = useCallback(() => setCustomDialogOpen(false), []);

  const openUnitSelectDialog = useCallback(() => setUnitSelectDialogOpen(true), []);
  const closeUnitSelectDialog = useCallback(() => setUnitSelectDialogOpen(false), []);

  const handleMenuClose = useCallback(
    (permission?: UnitPermissionLike | null) => {
      setMenuAnchorEl(null);
      closeUnitSelectDialog();
      if (!permission) {
        // ha félrekattintunk, vagy nem lenne value (ami nem történhet meg, mert akkor nem jönne elő a menü)
        return;
      }
      const { unitid, expires, company, hwinfo } = permission;
      const newValue = input.value ? [...input.value] : [];
      const index = newValue.findIndex(({ unitid: uid }) => uid === unitid);

      // módosítsuk/adjuk hozzá az adott permissiont vagy töröljük a listából
      if (expires === undefined) {
        newValue.splice(index, 1);
      } else if (index === -1) {
        newValue.push({ unitid, expires: expires ? expires.toISOString() : expires, company, hwinfo });
      } else {
        newValue[index] = { unitid, expires: expires ? expires.toISOString() : expires, company, hwinfo };
      }

      // változott az initial értékhez képest?
      let changed = newValue.length !== initialValuesMap.size;
      for (let i = 0; i < newValue.length && !changed; i++) {
        const curr = newValue[i];
        changed =
          !initialValuesMap.has(curr.unitid) ||
          (curr.expires === null) !== (initialValuesMap.get(curr.unitid) === null) ||
          (curr.expires !== null && !isEqual(new Date(curr.expires), initialValuesMap.get(curr.unitid) as Date));
      }
      if (changed) {
        input.onChange(newValue);
      } else {
        input.onChange(initial);
      }
    },
    [closeUnitSelectDialog, initial, initialValuesMap, input]
  );
  const openMenu = useCallback((value: UnitPermissionLike, element: HTMLElement) => {
    setMenuAnchorEl(element);
    setPermissionMenuValue(value);
  }, []);

  const getStyle = useCallback(
    (item: UnitPermissionLike) => {
      if (item.edited) {
        return {
          color: editedColor,
          backgroundColor: editedBackground,
        };
      }
      const valid = item.expires === null || isAfter(item.expires as Date, new Date());
      let color = valid ? theme.palette.common.black : theme.palette.text.disabled;
      let backgroundColor = valid ? theme.palette.common.white : theme.palette.grey["100"];

      return { color, backgroundColor };
    },
    [editedBackground, editedColor, theme]
  );

  const handleDateSelect = useCallback(
    (dateValue: Date) => {
      if (!permissionMenuValue) {
        return;
      }
      const { unitid, company, hwinfo, edited } = permissionMenuValue;
      handleMenuClose({ unitid, expires: dateValue, company, hwinfo, edited });
    },
    [handleMenuClose, permissionMenuValue]
  );

  const rowProps = useCallback(
    (row: Row<UnitPermissionLike>) => ({
      style: getStyle(row.original),
    }),
    [getStyle]
  );

  const columns = useMemo(
    (): Column<UnitPermissionLike>[] => [
      {
        id: "unitid",
        Header: formatMessage({
          defaultMessage: "Helios ID",
        }),
        accessor: (item) => item.unitid,
        width: 60,
      },
      {
        id: "expires",
        Header: formatMessage({
          defaultMessage: "Valid",
        }),
        accessor: (item) =>
          item.expires === null
            ? formatMessage({ defaultMessage: "Until revokation" })
            : item.expires === undefined
            ? formatMessage({ defaultMessage: "Until saving" })
            : formatDate(item.expires, dateFormatOptions),
        Cell: ({ row }: CellProps<UnitPermissionLike>) => (
          <span style={{ overflow: "hidden" }}>
            <Typography component={"span"} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.values.expires}
            </Typography>
          </span>
        ),
        width: 100,
      },
      {
        id: "company",
        Header: formatMessage({
          defaultMessage: "Owner company",
        }),
        accessor: (item) => item.company,
        Cell: ({ row }: CellProps<UnitPermissionLike>) => (
          <span style={{ overflow: "hidden" }}>
            <Typography component={"span"} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.values.company}
            </Typography>
          </span>
        ),
        width: 120,
      },
      {
        id: "hwinfo",
        Header: formatMessage({
          defaultMessage: "Hardware",
        }),
        accessor: (item) => item.hwinfo,
        Cell: ({ row }: CellProps<UnitPermissionLike>) => (
          <span style={{ overflow: "hidden" }}>
            <Typography component={"span"} sx={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>
              {row.values.hwinfo}
            </Typography>
          </span>
        ),
        width: 120,
      },
    ],
    [formatDate, formatMessage]
  );

  const unitSelectDialog = useMemo(() => {
    return (
      <UnitPermissionSelectWithFullSearchDialog
        open={unitSelectDialogOpen}
        onClose={closeUnitSelectDialog}
        onSelect={openMenu}
        currentPermissions={sortedPermissions}
      />
    );
  }, [closeUnitSelectDialog, openMenu, sortedPermissions, unitSelectDialogOpen]);

  return (
    <>
      <EnhancedTable
        columns={columns}
        data={sortedPermissions}
        showResultCount
        getRowProps={rowProps}
        queryStatus={userDataQueryStatus}
        overscanCount={5}
        limit={deviceLimit}
        sx={{ flex: 1 }}
        headerSx={{ borderTopLeftRadius: 0, borderTopRightRadius: 0 }}
        actionCellWidth={52}
        actionPosition="left"
        action={({ original }) => {
          return original.expires === null || (original.expires !== undefined && isAfter(new Date(original.expires), new Date())) ? (
            <IconButton size="small" sx={{ color: (theme) => theme.palette.success.light }} onClick={(event) => openMenu({ ...original }, event.currentTarget)}>
              <CheckIcon />
            </IconButton>
          ) : (
            <IconButton
              size="small"
              sx={{ color: (theme) => theme.palette.secondary.main }}
              onClick={(event) => openMenu({ ...original }, event.currentTarget)}
            >
              <AddIcon />
            </IconButton>
          );
        }}
        postfix={
          <IconButton
            size="small"
            sx={{
              color: "primary.contrastText",
            }}
            onClick={openUnitSelectDialog}
          >
            <Tooltip
              title={formatMessage({
                defaultMessage: "Add new unit permission",
              })}
            >
              <AddIcon />
            </Tooltip>
          </IconButton>
        }
      />
      <PermissionMenu
        anchorEl={menuAnchorEl}
        onClose={handleMenuClose}
        value={permissionMenuValue}
        initialValues={initialValuesMap}
        onCustom={openCustomDialog}
      />
      <CustomDateDialog open={customDialogOpen} onClose={closeCustomDialog} onDateSelect={handleDateSelect} />
      {unitSelectDialog}
    </>
  );
};
